import { CreateUserStoreSettingsNode, Node } from '@/api/dashboard'
import { Badge, CheckBoxContainer, FieldGroup, InputExtraMark, SelectMulti } from '@/ui'
import { BlockError } from '../types'
import { useTranslation } from 'react-i18next'
import { useStoresQuery } from '@/layouts/store/api'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import SettingsHeader from '@/layouts/campaigns/SettingsHeader'
import { ErrorMessage } from '@hookform/error-message'
import { FieldSection } from 'src/components/ui/FieldSection'
import { Divider, DurationSelectorDHMS } from '@/components'
import { useGameSettingsQuery } from '@/api/useGameSettingsQuery'
import { AnimatePresence, motion } from 'framer-motion'
import { Checkbox, FormErrorMessage, Input, Select, Textarea, cn, formatHookFormErrors } from '@dashboard/ui'
import { useContext, useEffect, useState } from 'react'
import { BonusItemBenefitEditor } from '@/layouts/components/BonusItemBenefitEditor'
import { useGenerateCompanyPath } from '@/libs'
import { SelectSkuTable } from '@/layouts/components/SelectSkuTable'
import { getDisabledClasses } from '@/layouts/campaigns/util'
import { CampaignContext, ICampaignContext } from '@/expr/Context'
import { stageMotionProps } from '@/components/StageMotionProps'
import { useQuery } from '@tanstack/react-query'
import { itemCategoriesQuery } from '@/layouts/item-category'

enum PromotionType {
  Items = 'items',
  Stores = 'stores',
  Categories = 'categories',
}

export default function InitStoreSettingsEditor(props: {
  node: CreateUserStoreSettingsNode
  error?: BlockError | null
  setNode: (node: Node) => void
  onClose: () => void
}) {
  const { t } = useTranslation()
  const context = useContext(CampaignContext) as ICampaignContext
  const { companyId, gameId } = useGenerateCompanyPath()
  const { data: stores = [] } = useStoresQuery(companyId, gameId, {
    limit: 100,
  })
  const { data: categories = [] } = useQuery({
    ...itemCategoriesQuery(companyId, gameId, { limit: 100 }),
    refetchOnMount: false,
  })
  const { data: gameSettings } = useGameSettingsQuery(companyId, gameId)
  const [promotionType, setPromotionType] = useState<PromotionType>(PromotionType.Stores)

  const methods = useForm<CreateUserStoreSettingsNode>({
    values: {
      ...props.node,
      store_ids: props.node.store_ids || (props.node.store_id ? [props.node.store_id] : []),
    },
  })

  const {
    handleSubmit,
    formState: { errors },
    control,
    register,
    watch,
    setValue,
    clearErrors,
    setError,
  } = methods

  useEffect(() => {
    if (props.node?.store_ids) {
      setPromotionType(PromotionType.Stores)
    }

    if (props.node?.item_ids) {
      setPromotionType(PromotionType.Items)
    }

    if (props.node?.category_ids) {
      setPromotionType(PromotionType.Categories)
    }
  }, [props.node.id])

  useEffect(() => {
    let err = props.error?.errors?.find(it => it.field == 'store_ids')
    if (err) {
      setError('store_ids', err)
    }
    err = props.error?.errors?.find(it => it.field == 'item_ids')
    if (err) {
      setError('item_ids', err)
    }
  }, [props.error])

  const [max_purchase_count, store_ids, item_ids, discount_percent, category_ids] = watch([
    'max_purchase_count',
    'store_ids',
    'item_ids',
    'discount_percent',
    'category_ids',
  ])

  const onSaveClick = (data: CreateUserStoreSettingsNode) => {
    if (!data.bonus_percent) {
      data.bonus_percent = null as unknown as number
    }

    if (!data.reward_points_percent) {
      data.reward_points_percent = null as unknown as number
    }

    if (!data.discount_percent) {
      data.discount_percent = null as unknown as number
    }

    props.setNode(data as Node)

    props.onClose()
  }

  const renderQuantityLimitations = () => {
    return (
      <FieldSection label={t('campaign.block.CreateUserStoreSettingsNode.quantity-limitations')}>
        <CheckBoxContainer
          checked={max_purchase_count != undefined}
          onChange={v => {
            setValue('max_purchase_count', v.target.checked ? 1 : undefined)
          }}
          label={t('campaign.block.CreateUserStoreSettingsNode.limit')}
        >
          {t('campaign.block.CreateUserStoreSettingsNode.max_purchases')}
          <Input type="number" {...register('max_purchase_count', { min: 1 })} />
        </CheckBoxContainer>
      </FieldSection>
    )
  }

  const renderGeneral = () => {
    return (
      <FieldSection label={t('campaign.block.CreateUserStoreSettingsNode.Benefits')}>
        <FieldGroup label={t('campaign.virtual-sku-editor.discount')} className="flex flex-col gap-1.5">
          <Input
            type="number"
            {...register('discount_percent', {
              min: {
                value: 1,
                message: t('validation.min', { field: t('store-promotions.discount_percent'), value: 1 }),
              },
              max: {
                value: 99,
                message: t('validation.max', { field: t('store-promotions.discount_percent'), value: 100 }),
              },
            })}
            extraLeft={{
              icon: <InputExtraMark>%</InputExtraMark>,
            }}
            {...formatHookFormErrors(errors, 'discount_percent')}
          />
          <Checkbox {...register('exclude_items_for_reward_points')} disabled={!discount_percent}>
            {t('campaign.block.CreateUserStoreSettingsNode.exclude_items_for_reward_points')}
          </Checkbox>
        </FieldGroup>

        <FieldGroup
          label={t('campaign.virtual-sku-editor.bonus')}
          caption={t('campaign.block.CreateUserStoreSettingsNode.bonus')}
        >
          <Input
            type="number"
            {...register('bonus_percent')}
            extraLeft={{
              icon: <InputExtraMark>%</InputExtraMark>,
            }}
          />
        </FieldGroup>

        {gameSettings?.enable_reward_points && (
          <FieldGroup label={t('campaign.virtual-sku-editor.reward_point_percent')}>
            <Input
              type="number"
              {...register('reward_points_percent', {
                min: { value: 1, message: t('campaign.virtual-sku-editor.reward_point_percent.validation') },
                max: {
                  value: 100,
                  message: t('campaign.virtual-sku-editor.reward_point_percent.validation'),
                },
              })}
              extraLeft={{
                icon: <InputExtraMark>%</InputExtraMark>,
              }}
            />
            <ErrorMessage
              name="reward_points_percent"
              errors={errors}
              render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
            />
          </FieldGroup>
        )}
      </FieldSection>
    )
  }

  const renderTimelimit = () => {
    return (
      <FieldSection label={t('campaign.block.CreateUserStoreSettingsNode.TimeLimitation')}>
        <Controller
          name="duration"
          control={control}
          rules={{ required: t('validation.required') }}
          render={({ field }) => (
            <FieldGroup label={t('campaign.block.CreateUserStoreSettingsNode.Duration')}>
              <DurationSelectorDHMS duration={field.value} size="small" onChange={v => field.onChange(v)} />
              <ErrorMessage
                name="duration"
                errors={errors}
                render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
              />
            </FieldGroup>
          )}
        />
      </FieldSection>
    )
  }

  const renderStoreIds = () => {
    return (
      <FieldGroup label={t('store.store')} caption={t('campaign.block.CreateUserStoreSettingsNode.warning')}>
        <Controller
          name="store_ids"
          control={control}
          rules={{ required: t('validation.required') }}
          render={({ field }) => (
            <SelectMulti
              items={stores.map(s => ({
                value: s.id,
                children: (
                  <div className="flex items-center gap-2">
                    {s.name}
                    <Badge variant="gray-secondary">{s.item_count}</Badge>
                  </div>
                ) as unknown as string,
                extraRight: t(`store.type.${s.type}`),
              }))}
              onChange={v => {
                clearErrors('store_ids')
                field.onChange(v)
              }}
              value={field.value || []}
            />
          )}
        />
        <ErrorMessage
          name="store_ids"
          errors={errors}
          render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
        />
      </FieldGroup>
    )
  }

  const renderCategoryIds = () => {
    return (
      <FieldGroup label={t('sku.item.category')}>
        <Controller
          name="category_ids"
          control={control}
          rules={{ required: t('validation.required') }}
          render={({ field }) => (
            <SelectMulti
              valueMode={'multiple'}
              items={categories.map(s => ({
                value: s.id,
                children: s.name,
              }))}
              onChange={v => {
                clearErrors('category_ids')
                field.onChange(v)
              }}
              value={field.value || []}
            />
          )}
        />
        <ErrorMessage
          name="category_ids"
          errors={errors}
          render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
        />
      </FieldGroup>
    )
  }

  const renderItemsIds = () => {
    return (
      <div>
        <Controller
          name="item_ids"
          control={control}
          rules={{ required: t('validation.required') }}
          render={({ field }) => (
            <SelectSkuTable
              onChange={v => {
                clearErrors('item_ids')
                field.onChange(v)
              }}
              value={field.value || []}
            />
          )}
        />
        <ErrorMessage
          name="item_ids"
          errors={errors}
          render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
        />
      </div>
    )
  }

  const renderItemsType = () => {
    return (
      <FieldSection label={t('store-promotions.item-type')} caption={t('store-promotions.item-caption')}>
        <FieldGroup label={t('store-promotions.type')}>
          <Select
            items={[
              ...Object.values(PromotionType).map(item => ({
                children: t(`store-promotions.type.${item}`),
                value: item,
              })),
            ]}
            value={promotionType}
            onChange={v => {
              setPromotionType(v as PromotionType)
              if (v !== PromotionType.Stores) {
                setValue('store_ids', null as unknown as string[], { shouldDirty: true })
              }
              if (v !== PromotionType.Items) {
                setValue('item_ids', null as unknown as string[], { shouldDirty: true })
              }
              if (v !== PromotionType.Categories) {
                setValue('category_ids', null as unknown as string[], { shouldDirty: true })
              }
            }}
          />
        </FieldGroup>

        {promotionType === PromotionType.Stores && renderStoreIds()}
        {promotionType === PromotionType.Items && renderItemsIds()}
        {promotionType === PromotionType.Categories && renderCategoryIds()}
      </FieldSection>
    )
  }

  return (
    <div>
      <SettingsHeader onSave={handleSubmit(onSaveClick)} onClose={props.onClose} node={props.node as Node} />
      <div className={cn('flex flex-col gap-3 p-6', getDisabledClasses(context.readOnly))}>
        <div>{renderItemsType()}</div>
        <AnimatePresence>
          {((store_ids && store_ids.length > 0) ||
            (item_ids && item_ids.length > 0) ||
            (category_ids && category_ids.length > 0)) && (
            <motion.div {...stageMotionProps}>
              <Divider />

              {renderGeneral()}

              <Divider />

              <FormProvider {...methods}>
                <BonusItemBenefitEditor />
              </FormProvider>
              <Divider />

              {renderQuantityLimitations()}

              <Divider />

              {renderTimelimit()}

              <Divider />
              <FieldSection label={t('campaign.description')}>
                <FieldGroup label={t('campaign.block.description.tooltip')}>
                  <Textarea {...register('description')} />
                </FieldGroup>
              </FieldSection>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}
